import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getAdditionalUserInfo,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  reload,
  updateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { clearCdnCache } from './cdnAuth';

// Map Firebase auth error codes to user-friendly messages and custom codes
const mapAuthError = (error) => {
  const errorMap = {
    'auth/email-already-in-use': {
      message: 'Email already in use. Please log in instead.',
      code: 'EMAIL_EXISTS',
    },
    'auth/invalid-credential': {
      message: 'Invalid credentials',
      code: 'INVALID_CREDENTIALS',
    },
    'auth/user-not-found': {
      message: 'No account found with this email',
      code: 'USER_NOT_FOUND',
    },
    'auth/too-many-requests': {
      message: 'Too many attempts. Please try again later.',
      code: 'TOO_MANY_REQUESTS',
    },
    'auth/network-request-failed': {
      message: 'Network error. Please check your connection.',
      code: 'NETWORK_ERROR',
    },
    'auth/requires-recent-login': {
      message: 'Session expired. Please log in again.',
      code: 'REAUTH_REQUIRED',
    },
  };

  const defaultError = {
    message: 'An error occurred. Please try again.',
    code: error.code || 'UNKNOWN_ERROR',
  };

  const mappedError = errorMap[error.code] || defaultError;
  const authError = new Error(mappedError.message);
  authError.code = mappedError.code;
  authError.originalError = error;

  return authError;
};

export const GOOGLE_SIGN_IN_CANCELLED_CODE = 'GOOGLE_SIGN_IN_CANCELLED';
const PENDING_SIGNUP_STORAGE_KEY = 'auth:pendingSignup';
const PENDING_LOGIN_LANGUAGE_NOTICE_KEY = 'auth:pendingLoginLanguageNotice';
const PENDING_BACKEND_LOGIN_SYNC_KEY = 'auth:pendingBackendLoginSync';
const PENDING_EMAIL_SIGNUP_ANALYTICS_PREFIX = 'analytics:pendingEmailSignup:';
const SIGNUP_TRACKED_ANALYTICS_PREFIX = 'analytics:signupTracked:';
const ALLOWED_SIGNUP_LANGUAGES = new Set(['EN', 'FR', 'DE']);

const getSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch (_) {
    return null;
  }
};

const getLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch (_) {
    return null;
  }
};

const analyticsStorageKey = (prefix, uid) => (uid ? `${prefix}${uid}` : null);

export const stagePendingEmailSignupTracking = (uid) => {
  const storage = getLocalStorage();
  const key = analyticsStorageKey(PENDING_EMAIL_SIGNUP_ANALYTICS_PREFIX, uid);
  if (!storage || !key) return false;

  try {
    storage.setItem(key, '1');
    return true;
  } catch (_) {
    return false;
  }
};

export const shouldTrackPendingEmailSignup = (uid) => {
  const storage = getLocalStorage();
  const pendingKey = analyticsStorageKey(PENDING_EMAIL_SIGNUP_ANALYTICS_PREFIX, uid);
  const trackedKey = analyticsStorageKey(SIGNUP_TRACKED_ANALYTICS_PREFIX, uid);
  if (!storage || !pendingKey || !trackedKey) return false;

  try {
    return storage.getItem(pendingKey) === '1' && storage.getItem(trackedKey) !== '1';
  } catch (_) {
    return false;
  }
};

export const completePendingEmailSignupTracking = (uid) => {
  const storage = getLocalStorage();
  const pendingKey = analyticsStorageKey(PENDING_EMAIL_SIGNUP_ANALYTICS_PREFIX, uid);
  const trackedKey = analyticsStorageKey(SIGNUP_TRACKED_ANALYTICS_PREFIX, uid);
  if (!storage || !pendingKey || !trackedKey) return false;

  try {
    storage.setItem(trackedKey, '1');
    storage.removeItem(pendingKey);
    return true;
  } catch (_) {
    return false;
  }
};

const normalizePendingSignupMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }

  const sourceLang = typeof metadata.sourceLang === 'string' ? metadata.sourceLang.trim().toUpperCase() : null;
  const targetLang = typeof metadata.targetLang === 'string' ? metadata.targetLang.trim().toUpperCase() : null;
  const communicateImportantUpdates =
    typeof metadata.communicateImportantUpdates === 'boolean' ? metadata.communicateImportantUpdates : undefined;

  if (!sourceLang || !targetLang) {
    return null;
  }

  if (!ALLOWED_SIGNUP_LANGUAGES.has(sourceLang) || !ALLOWED_SIGNUP_LANGUAGES.has(targetLang)) {
    return null;
  }

  return {
    sourceLang,
    targetLang,
    ...(communicateImportantUpdates === undefined ? {} : { communicateImportantUpdates }),
    ...(typeof metadata.targetExamLevel === 'string' && metadata.targetExamLevel.trim()
      ? { targetExamLevel: metadata.targetExamLevel.trim().toUpperCase() }
      : {}),
    ...(typeof metadata.examDate === 'string' && metadata.examDate.trim()
      ? { examDate: metadata.examDate.trim() }
      : {}),
  };
};

export function stagePendingSignupMetadata(metadata) {
  const normalizedMetadata = normalizePendingSignupMetadata(metadata);
  const storage = getSessionStorage();

  if (!normalizedMetadata || !storage) {
    return null;
  }

  storage.setItem(PENDING_SIGNUP_STORAGE_KEY, JSON.stringify(normalizedMetadata));
  return normalizedMetadata;
}

export function readPendingSignupMetadata() {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }

  const rawValue = storage.getItem(PENDING_SIGNUP_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const normalizedMetadata = normalizePendingSignupMetadata(JSON.parse(rawValue));
    if (!normalizedMetadata) {
      storage.removeItem(PENDING_SIGNUP_STORAGE_KEY);
      return null;
    }
    return normalizedMetadata;
  } catch (_) {
    storage.removeItem(PENDING_SIGNUP_STORAGE_KEY);
    return null;
  }
}

export function clearPendingSignupMetadata() {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  storage.removeItem(PENDING_SIGNUP_STORAGE_KEY);
}

export function stagePendingLoginLanguageNotice() {
  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }

  storage.setItem(PENDING_LOGIN_LANGUAGE_NOTICE_KEY, '1');
  return true;
}

export function consumePendingLoginLanguageNotice() {
  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }

  const hasPendingNotice = storage.getItem(PENDING_LOGIN_LANGUAGE_NOTICE_KEY) === '1';
  storage.removeItem(PENDING_LOGIN_LANGUAGE_NOTICE_KEY);
  return hasPendingNotice;
}

export function clearPendingLoginLanguageNotice() {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  storage.removeItem(PENDING_LOGIN_LANGUAGE_NOTICE_KEY);
}

export function stagePendingBackendLoginSync() {
  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(PENDING_BACKEND_LOGIN_SYNC_KEY, '1');
    return true;
  } catch (_) {
    return false;
  }
}

export function consumePendingBackendLoginSync() {
  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }

  try {
    const hasPendingSync = storage.getItem(PENDING_BACKEND_LOGIN_SYNC_KEY) === '1';
    storage.removeItem(PENDING_BACKEND_LOGIN_SYNC_KEY);
    return hasPendingSync;
  } catch (_) {
    return false;
  }
}

export function clearPendingBackendLoginSync() {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(PENDING_BACKEND_LOGIN_SYNC_KEY);
  } catch (_) {
    // Storage may be unavailable.
  }
}

export async function signUpWithEmailPassword(email, password) {
  try {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    // Send verification email with explicit continue URL
    await sendEmailVerification(cred.user, {
      url: `${window.location.origin}/auth/verify?verified=1`,
      handleCodeInApp: false,
    });
    return cred.user;
  } catch (error) {
    throw mapAuthError(error);
  }
}

export async function signInWithEmailPassword(email, password) {
  stagePendingLoginLanguageNotice();
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    // Ensure latest verification status
    await reload(cred.user);
    if (!cred.user.emailVerified) {
      throw { code: 'auth/email-not-verified' };
    }
    return cred.user;
  } catch (error) {
    clearPendingLoginLanguageNotice();
    if (error.code === 'auth/email-not-verified') {
      throw error;
    }
    throw mapAuthError(error);
  }
}

export async function requestPasswordReset(email) {
  try {
    await sendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/auth/login`,
      handleCodeInApp: false,
    });
  } catch (error) {
    // Keep the result private: a missing email/password account must look the
    // same as a successful request. This also covers Google-only accounts.
    if (error?.code === 'auth/user-not-found') {
      return;
    }
    throw mapAuthError(error);
  }
}

export async function signInWithGoogle() {
  stagePendingLoginLanguageNotice();
  try {
    const cred = await signInWithPopup(auth, googleProvider);
    return {
      user: cred.user,
      isNewUser: getAdditionalUserInfo(cred)?.isNewUser === true,
    };
  } catch (e) {
    // Only fall back to redirect when the popup was blocked by the browser.
    if (e?.code === 'auth/popup-blocked') {
      await signInWithRedirect(auth, googleProvider);
      return null; // flow continues after redirect
    }
    if (e?.code === 'auth/popup-closed-by-user') {
      clearPendingLoginLanguageNotice();
      const cancelledError = new Error('Google sign-in was cancelled.');
      cancelledError.code = GOOGLE_SIGN_IN_CANCELLED_CODE;
      cancelledError.originalError = e;
      throw cancelledError;
    }
    clearPendingLoginLanguageNotice();
    throw e;
  }
}

export async function resendVerificationEmail(user) {
  if (!user || typeof user.getIdToken !== 'function') {
    throw new Error('User not properly authenticated');
  }

  try {
    // Force refresh the user to get the latest data
    await user.reload();

    // Check if already verified
    if (user.emailVerified) {
      return { success: true, message: 'Email already verified' };
    }

    // Send verification email
    await sendEmailVerification(user, {
      url: window.location.origin, // Use current origin as continue URL
      handleCodeInApp: false,
    });

    return { success: true, message: 'Verification email sent' };
  } catch (error) {
    console.error('Error resending verification email:', error);
    throw error;
  }
}

export async function logout() {
  clearCdnCache();
  await signOut(auth);
}

// Update the current user's email and resend verification.
// If recent login is required, this will throw with code 'auth/requires-recent-login'.
// Callers can reattempt providing the current password to reauthenticate.
export async function changeEmailAndResend(newEmail, passwordIfNeeded) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be signed in to change your email.');
  }
  try {
    await updateEmail(user, newEmail);
  } catch (e) {
    if (e?.code === 'auth/requires-recent-login') {
      if (!passwordIfNeeded) {
        const err = new Error('Reauthentication required');
        err.code = 'auth/requires-recent-login';
        throw err;
      }
      const cred = EmailAuthProvider.credential(user.email, passwordIfNeeded);
      await reauthenticateWithCredential(user, cred);
      await updateEmail(user, newEmail);
    } else {
      throw e;
    }
  }
  // After updating email, send a fresh verification email.
  await sendEmailVerification(user, { handleCodeInApp: false });
  // Ensure client reflects latest state
  await reload(user);
  return true;
}
